import { Injectable } from "@angular/core";
import { Action, State, StateContext } from "@ngxs/store";

import { ClienteDTO, ClientePermisoTemplateDTO } from "@DTOS";
import { ClientesService } from "../../../layout/clientes/clientes.service";
import { CustomState } from "../CustomState";
import { ClientesAcciones } from "./clientes.actions";
import { EstadoState } from "../EstadoState";
import { EstadoEnum } from "../entidades";
import { Nilable } from "@shared/utils";
import { AccionEnum } from "@shared/enums";

export interface IClientesState extends EstadoState<ClienteDTO> {
    clientes: ClienteDTO[];
    logos: string[];
    cliente: Nilable<ClienteDTO>;
}

@State<IClientesState>({
    name: "clientes",
    defaults: {
        clientes: [],
        logos: [],
        cliente: null,
        estadoAccion: null
    }
})
@Injectable()
export class ClientesState extends CustomState<IClientesState> {
    private cargaClientes = false;
    private clientes: ClienteDTO[];
    private clientesTemplates: Map<number, ClientePermisoTemplateDTO[]>;

    constructor(private clientesService: ClientesService) {
        super();
    }

    @Action(ClientesAcciones.Obtener)
    public async obtenerClientes(ctx: StateContext<IClientesState>) {
        const stateNuevo = this.obtenerNuevoState(ctx);

        if (!this.cargaClientes) {
            this.clientes = await this.clientesService.obtenerClientes();
            stateNuevo.clientes = this.clientes;
            this.cargaClientes = true;
        }

        stateNuevo.clientes = [...this.clientes];

        ctx.setState(stateNuevo);
    }

    @Action(ClientesAcciones.ObtenerId)
    public async obtenerClientePorId(ctx: StateContext<IClientesState>, action: ClientesAcciones.ObtenerId) {
        const stateNuevo = this.obtenerNuevoState(ctx);

        if (this.cargaClientes) {
            stateNuevo.cliente = this.clientes.find(cliente => cliente.id === action.id) ?? null;
        } else {
            const cliente = await this.clientesService.getCliente(action.id);
            this.clientes = [...this.clientes || [], cliente];
            stateNuevo.cliente = cliente;
        }

        stateNuevo.clientes = [...this.clientes];

        ctx.setState(stateNuevo);
    }

    @Action(ClientesAcciones.PedirEntidades)
    public async pedirEntidades(ctx: StateContext<IClientesState>, action: ClientesAcciones.PedirEntidades) {
        if (!this.cargaClientes) {
            await this.obtenerClientes(ctx);
        }

        ctx.dispatch(new action.accion(this.clientes));
    }

    @Action(ClientesAcciones.Filtrar)
    public filtrarClientes(ctx: StateContext<IClientesState>, action: ClientesAcciones.Filtrar) {
        const stateNuevo = this.obtenerNuevoState(ctx);

        stateNuevo.clientes = this.clientes.filter(action.filtroFuncion);

        ctx.setState(stateNuevo);
    }

    @Action(ClientesAcciones.Ordenar)
    public ordenarClientes(ctx: StateContext<IClientesState>, action: ClientesAcciones.Ordenar) {
        const stateNuevo = this.obtenerNuevoState(ctx);

        stateNuevo.clientes = [...this.clientes].sort(action.ordenFuncion);

        ctx.setState(stateNuevo);
    }

    @Action(ClientesAcciones.Agregar)
    public async agregarCliente(ctx: StateContext<IClientesState>, action: ClientesAcciones.Agregar) {
        const stateNuevo = this.obtenerNuevoState(ctx);

        const cliente = await this.clientesService.agregarCliente(action.cliente);

        if (cliente != null) {
            this.clientes = [...this.clientes || [], cliente];
        }
        stateNuevo.estadoAccion = {
            accion: AccionEnum.AGREGAR,
            estado: cliente != null ? EstadoEnum.CORRECTO : EstadoEnum.ERROR,
            entidad: cliente
        };
        stateNuevo.clientes = [...this.clientes];


        ctx.setState(stateNuevo);
    }

    @Action(ClientesAcciones.Actualizar)
    public async actualizarCliente(ctx: StateContext<IClientesState>, action: ClientesAcciones.Actualizar) {
        const stateNuevo = this.obtenerNuevoState(ctx);

        const clienteNuevo = await this.clientesService.guardarCliente(action.cliente);
        if (clienteNuevo != null) {
            this.clientes = this.clientes.map(cliente =>
                cliente.id === clienteNuevo.id ? clienteNuevo : cliente
            );
        }
        stateNuevo.estadoAccion = {
            accion: AccionEnum.ACTUALIZAR,
            estado: clienteNuevo != null ? EstadoEnum.CORRECTO : EstadoEnum.ERROR,
            entidad: clienteNuevo
        };
        stateNuevo.clientes = [...this.clientes];

        ctx.setState(stateNuevo);
    }

    @Action(ClientesAcciones.Borrar)
    public async borrarCliente(ctx: StateContext<IClientesState>, action: ClientesAcciones.Borrar) {
        const stateNuevo = this.obtenerNuevoState(ctx);

        const borro = await this.clientesService.borrarCliente(action.cliente);
        if (borro) {
            this.clientes = this.clientes.filter(cliente => cliente.id !== action.cliente.id);
            stateNuevo.clientes = this.clientes;
        }
        stateNuevo.estadoAccion = {
            accion: AccionEnum.BORRAR,
            estado: borro ? EstadoEnum.CORRECTO : EstadoEnum.ERROR
        };

        ctx.setState(stateNuevo);
    }

    @Action(ClientesAcciones.LimpiarEstadoAccion)
    public limpiarError(ctx: StateContext<IClientesState>) {
        const stateNuevo = this.obtenerNuevoState(ctx);

        stateNuevo.estadoAccion = null;

        ctx.setState(stateNuevo);
    }

    @Action(ClientesAcciones.ObtenerTemplates)
    public async obtenerTemplates(ctx: StateContext<IClientesState>) {}
}
